import React, { useState, useEffect, useRef } from "react";
import conhecaBadge from '../../../../assets/images/conheca-badge.png';
import ModalMessage from '../../Modal/ModalMessage';
import ModalConfirm from '../../Modal/ModalConfirm';
import { getImageUrl } from '../../../../utils/media';

import '../../../Home/HeroBanner/style.css';
import "./SectionSlideshow.css";

const AUTOPLAY_INTERVAL = 5000;

export default function SectionSlideshow({
  isEditing,
  slides,
  setSlides,
  fraseDestaque,
  setFraseDestaque,
  linkConheca,
  setLinkConheca,
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);
  const [modalMessage, setModalMessage] = useState('');
  const [isModalMessageOpen, setIsModalMessageOpen] = useState(false);
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);
  const [slideParaRemover, setSlideParaRemover] = useState(null);

  // Pausa o autoplay do carrossel caso o modo de edição esteja ativo
  useEffect(() => {
    if (slides.length === 0 || isEditing) return undefined;
    
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, AUTOPLAY_INTERVAL);

    return () => clearInterval(intervalRef.current);
  }, [slides.length, isEditing]);

  const goToSlide = (index) => setCurrentSlide(index);

  // Processa e valida o upload local para pré-visualização no slide ativo
  const handleImageUpload = (index, event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setModalMessage("Formato inválido. A imagem deve ser JPG ou PNG.");
      setIsModalMessageOpen(true);
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setModalMessage("A imagem é muito grande. O tamanho máximo permitido é 15MB.");
      setIsModalMessageOpen(true);
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const newSlides = [...slides];
    newSlides[index] = {
      ...newSlides[index],
      url: previewUrl,
      imageUrl: previewUrl,
      file: file,
      fallback: false,
    };
    setSlides(newSlides);
  };

  // Adiciona um novo slide em branco ao final da fila
  const handleAddSlide = () => {
    const novoSlide = {
      id: Date.now(),
      url: "",
      imageUrl: "",
      file: null,
    };
    const novosSlides = [...slides, novoSlide];
    setSlides(novosSlides);
    setCurrentSlide(novosSlides.length - 1);
  };

  // Remove o slide e reposiciona o índice do carrossel
  const handleRemoveSlide = (indexToRemove) => {
    setSlideParaRemover(indexToRemove);
    setIsModalConfirmOpen(true);
  };

  const handleConfirmRemoveSlide = () => {
    if (slideParaRemover !== null) {
      const novosSlides = slides.filter((_, idx) => idx !== slideParaRemover);
      setSlides(novosSlides);
      setCurrentSlide((prev) => (
        novosSlides.length === 0 ? 0 : Math.min(prev, novosSlides.length - 1)
      ));
    }
    setIsModalConfirmOpen(false);
    setSlideParaRemover(null);
  };

  return (
    <>
      <section className="hero-banner hero-banner--admin">
      <div className="hero-banner__track">
        {slides.length > 0 ? (
          slides.map((slide, index) => {
            const backgroundUrl = getImageUrl(
              slide?.imageUrl || slide?.url || slide,
            );
            const isActive = index === currentSlide;

            return (
              <div
                key={slide._id || slide.id || index}
                className={`hero-banner__slide ${isActive ? "hero-banner__slide--active" : ""}`}
              >
                {backgroundUrl && (
                  <img
                    className="hero-banner__slide-image"
                    src={backgroundUrl}
                    alt=""
                    aria-hidden="true"
                  />
                )}

                {/* Sobreposição de ações do administrador */}
                {isEditing && isActive && (
                  <div className="hero-banner__admin-overlay">
                    <div className="hero-banner__admin-actions">
                      <label className="btn-admin-action btn-admin-action--upload">
                        <input
                          type="file"
                          accept=".jpg, .jpeg, .png"
                          onChange={(e) => handleImageUpload(index, e)}
                          hidden
                        />
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5-7l-3 3.72L9 13l-3 4h14l-4-5z" />
                        </svg>
                        <span>Substituir Imagem do Slide {index + 1}</span>
                      </label>

                      <button
                        type="button"
                        className="btn-admin-action btn-admin-action--delete"
                        onClick={() => handleRemoveSlide(index)}
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                        <span>Excluir Slide</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="hero-banner__placeholder" />
        )}
      </div>

      {/* Inputs para a edição da frase em destaque e link */}
      <div className="hero-banner__badge">
        <div className="hero-banner__badge-content">
          <img src={conhecaBadge} alt={fraseDestaque || "Conheça o 7 Gerações!"} />
          
          {isEditing && (
            <div className="hero-banner__badge-edit-box">
              <div className="input-group-badge">
                <label>Frase em Destaque</label>
                <input
                  type="text"
                  value={fraseDestaque}
                  onChange={(e) => setFraseDestaque(e.target.value)}
                  placeholder="Conheça o 7 Gerações!"
                />
              </div>
              <div className="input-group-badge">
                <label>Link de Destino</label>
                <input
                  type="text"
                  value={linkConheca}
                  onChange={(e) => setLinkConheca(e.target.value)}
                  placeholder="/quem-somos"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navegação e contadores */}
      {slides.length > 1 && (
        <div className="hero-banner__dots">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`hero-banner__dot ${index === currentSlide ? "hero-banner__dot--active" : ""}`}
              onClick={() => goToSlide(index)}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {isEditing && (
        <button
          type="button"
          className="hero-banner__btn-add-slide"
          onClick={handleAddSlide}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          Adicionar Novo Slide
        </button>
      )}

      </section>

      {slides.length > 0 && (
        <div className="hero-banner__editor-preview" aria-label="Pré-visualização dos slides">
          <div className="hero-banner__editor-preview-header">
            <div>
              <strong>{isEditing ? 'Pré-visualização dos slides' : 'Slides cadastrados'}</strong>
              <span>{isEditing ? 'Selecione uma miniatura para editar aquele slide.' : 'Clique em uma miniatura para visualizar o slide.'}</span>
            </div>
            <span>{currentSlide + 1} de {slides.length}</span>
          </div>
          <div className="hero-banner__editor-thumbnails">
            {slides.map((slide, index) => {
              const previewUrl = getImageUrl(
                slide?.imageUrl || slide?.url || slide,
              );
              return (
                <button
                  key={slide._id || slide.id || index}
                  type="button"
                  className={`hero-banner__editor-thumbnail${index === currentSlide ? " is-active" : ""}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Editar slide ${index + 1}`}
                >
                  {previewUrl ? <img src={previewUrl} alt="" /> : <span>Sem imagem</span>}
                  <small>Slide {index + 1}</small>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ModalMessage
        isOpen={isModalMessageOpen}
        onClose={() => setIsModalMessageOpen(false)}
        message={modalMessage}
      />

      <ModalConfirm
        isOpen={isModalConfirmOpen}
        onClose={() => setIsModalConfirmOpen(false)}
        onConfirm={handleConfirmRemoveSlide}
        message="Confirmar a exclusão deste slide?"
      />
    </>
  );
}
